/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useState, useCallback, useEffect } from 'react';
import {
  AIGoal,
  NewsItem,
  StoryEvent,
  Scenario,
  BuildingType,
  Grid,
  CityStats,
} from '../types';
import { GRID_SIZE } from '../constants';
import {
  generateCityGoal,

  generateMissionBriefing,
  generateSpeech,
} from '../services/geminiService';
import { resolveRequestAiCoaching } from '../src/adapters/ui/hooks/useAiCoaching';
import { resolveGenerateAiNews } from '../src/adapters/ui/hooks/useAiNews';
import { playGeminiAudio, stopGeminiAudio } from '../services/audioService';
import { playUiSfx } from '../utils/uiAudio';
import { gameStore } from '../stores/gameStore';
import { simulationEngine } from '../src/domain/services/SimulationEngine';

export function useNewsFeed() {
  const [newsFeed, setNewsFeed] = useState<NewsItem[]>([]);

  const addNewsItem = useCallback((item: NewsItem) => {
    setNewsFeed((prev) => [...prev.slice(-12), item]);
    if (item.type === 'negative') playUiSfx('alert');
  }, []);

  return { newsFeed, setNewsFeed, addNewsItem };
}

export interface UseAiNarrativeDeps {
  aiEnabled: boolean;
  activeScenario: Scenario | null;
  stats: CityStats;
  grid: Grid;
  setGrid: (grid: Grid | ((prev: Grid) => Grid)) => void;
  setStats: (stats: CityStats | ((prev: CityStats) => CityStats)) => void;
  addNewsItem: (item: NewsItem) => void;
}

function isGoalMet(goal: AIGoal, stats: CityStats, grid: Grid): boolean {
  if (goal.targetType === 'compliance') {
    return stats.compliance >= goal.targetValue;
  }
  if (goal.targetType === 'budget') {
    return stats.budget >= goal.targetValue;
  }
  if (goal.targetType === 'building_count' && goal.buildingType) {
    const count = grid.flat().filter((tile) => tile.buildingType === goal.buildingType).length;
    return count >= goal.targetValue;
  }
  return false;
}

export function useAiNarrative({
  aiEnabled,
  activeScenario,
  stats,
  grid,
  setGrid,
  setStats,
  addNewsItem,
}: UseAiNarrativeDeps) {
  const [currentGoal, setCurrentGoal] = useState<AIGoal | null>(null);
  const [isGeneratingGoal, setIsGeneratingGoal] = useState(false);
  const [currentStory, setCurrentStory] = useState<StoryEvent | null>(null);

  useEffect(() => {
    if (!currentGoal || currentGoal.completed) return;

    if (isGoalMet(currentGoal, stats, grid)) {
      setCurrentGoal((prev) => (prev ? { ...prev, completed: true } : null));
      addNewsItem({
        id: Date.now().toString(),
        text: 'MANDATE COMPLETE: Awaiting budget authorization.',
        type: 'positive',
      });
    }
  }, [currentGoal, stats, grid, addNewsItem]);

  const handleClaimReward = useCallback(() => {
    if (!currentGoal?.completed) return;

    const reward = currentGoal.reward;
    setStats((prev) => ({ ...prev, budget: prev.budget + reward }));
    addNewsItem({
      id: Date.now().toString(),
      text: `BUDGET AUTHORIZED: +$${reward} transferred to treasury.`,
      type: 'positive',
    });
    setCurrentGoal(null);
    playUiSfx('success');
  }, [currentGoal, setStats, addNewsItem]);

  const triggerStory = useCallback(
    async (customText?: string) => {
      let text = customText;
      if (!text && aiEnabled) {
        text = (await generateMissionBriefing(gameStore.getState().stats)) || '';
      }

      if (text) {
        addNewsItem({
          id: Date.now().toString(),
          text: `DIRECTOR: ${text}`,
          type: 'neutral',
        });
        if (aiEnabled) {
          stopGeminiAudio();
          const audio = await generateSpeech(text);
          if (audio) {
            setCurrentStory({
              id: Date.now().toString(),
              text,
              audioBase64: audio,
              isPlaying: true,
            });
            const source = await playGeminiAudio(audio);
            if (source) {
              source.onended = () => {
                setCurrentStory((prev) => (prev ? { ...prev, isPlaying: false } : null));
              };
            } else {
              setCurrentStory((prev) => (prev ? { ...prev, isPlaying: false } : null));
            }
          }
        }
      }
    },
    [addNewsItem, aiEnabled]
  );

  const fetchNewGoal = useCallback(async () => {
    if (activeScenario || isGeneratingGoal || !aiEnabled || currentGoal) return;
    setIsGeneratingGoal(true);
    const snap = gameStore.getState();
    const newGoal = await generateCityGoal(snap.stats, snap.grid);
    if (newGoal) {
      setCurrentGoal(newGoal);
      triggerStory(`New Mandate received: ${newGoal.description}`);
    }
    setIsGeneratingGoal(false);
  }, [isGeneratingGoal, triggerStory, activeScenario, currentGoal, aiEnabled]);

  const fetchNews = useCallback(async () => {
    const result = await resolveGenerateAiNews({
      stats: gameStore.getState().stats,
      aiEnabled,
    });
    if (result.news) addNewsItem(result.news);
  }, [addNewsItem, aiEnabled]);

  const handleAskAICoach = useCallback(
    async (objective: string) => {
      const result = await resolveRequestAiCoaching({ objective, aiEnabled });
      if (!result.ok) return;

      if (result.pendingMessage) {
        addNewsItem({
          id: Date.now().toString(),
          text: result.pendingMessage,
          type: 'neutral',
        });
      }
      if (result.tip) {
        addNewsItem({
          id: Date.now().toString(),
          text: `COACH: ${result.tip}`,
          type: 'positive',
        });
      }
    },
    [aiEnabled, addNewsItem]
  );

  const handleAutoSolve = useCallback(() => {
    if (!activeScenario) return;
    const id = activeScenario.id;
    const center = Math.floor(GRID_SIZE / 2);

    setGrid((prev) => {
      const newGrid = prev.map((row) => row.map((tile) => ({ ...tile })));

      if (id === 'tut_1_basics') {
        [
          [center, center - 1],
          [center, center + 1],
          [center - 1, center],
        ].forEach(([y, x]) => {
          newGrid[y][x].buildingType = BuildingType.Pipeline;
          newGrid[y][x].isConnected = true;
        });
        newGrid[center][center - 2] = {
          ...newGrid[center][center - 2],
          buildingType: BuildingType.ComplianceMgmt,
          isConnected: true,
        };
        newGrid[center - 1][center + 1] = {
          ...newGrid[center - 1][center + 1],
          buildingType: BuildingType.OpsComplaints,
          isConnected: true,
        };

        addNewsItem({
          id: Date.now().toString(),
          text: 'AUTO-DEPLOY: Foundation layout established.',
          type: 'positive',
        });
      } else if (id === 'tut_2_economy') {
        newGrid[center + 1][center + 1] = {
          ...newGrid[center + 1][center + 1],
          buildingType: BuildingType.OpsFunding,
          isConnected: true,
        };
        newGrid[center + 1][center + 2] = {
          ...newGrid[center + 1][center + 2],
          buildingType: BuildingType.Road,
          isConnected: true,
        };
        addNewsItem({
          id: Date.now().toString(),
          text: 'AUTO-DEPLOY: Treasury and Road deployed.',
          type: 'positive',
        });
      } else if (id === 'tut_3_security') {
        newGrid[center - 2][center - 2] = {
          ...newGrid[center - 2][center - 2],
          buildingType: BuildingType.Firewall,
          isConnected: true,
        };
        const violTile = newGrid[center + 1][center - 1];
        if (violTile && violTile.buildingType === BuildingType.OpsComplaints) {
          violTile.buildingType = BuildingType.None;
        }
        addNewsItem({
          id: Date.now().toString(),
          text: 'AUTO-DEPLOY: Firewall active. Violation resolved.',
          type: 'positive',
        });
      } else if (id === 'tut_4_advanced') {
        addNewsItem({
          id: Date.now().toString(),
          text: 'AUTO-DEPLOY: Granting tech...',
          type: 'neutral',
        });
        newGrid[center - 2][center] = {
          ...newGrid[center - 2][center],
          buildingType: BuildingType.ZeroTrust,
          isConnected: true,
        };
      }

      const connected = simulationEngine.calculateConnectivity(newGrid);
      const metrics = simulationEngine.calculateTickMetrics(
        connected,
        stats,
        gameStore.getState().tickNumber
      );
      gameStore.setState({
        deltas: metrics.deltas,
        violationTiles: metrics.violationTiles,
      });

      return connected;
    });
    playUiSfx('build');
  }, [activeScenario, addNewsItem, stats, setGrid]);

  return {
    currentGoal,
    setCurrentGoal,
    isGeneratingGoal,
    currentStory,
    setCurrentStory,
    triggerStory,
    fetchNewGoal,
    fetchNews,
    handleAskAICoach,
    handleAutoSolve,
    handleClaimReward,
  };
}